import { createContext, PropsWithChildren, use } from 'react';
import { StyleSheet } from 'react-native';
import { ScrollView as RNGHScrollView } from 'react-native-gesture-handler';
import Animated, { AnimatedRef } from 'react-native-reanimated';
import { fullscreenStyle, GetProps, styled } from 'tamagui';

/**
 * Unstyled ScrollView to be used as renderScrollComponent
 */
export const ScrollViewBase = RNGHScrollView;

export type ScrollViewProps = GetProps<typeof ScrollView>;

export const ScrollView = styled(
  RNGHScrollView,
  {
    name: 'ScrollView',
    scrollEnabled: true,

    variants: {
      fullscreen: {
        true: fullscreenStyle
      }
    } as const
  },
  {
    accept: {
      contentContainerStyle: 'style'
    } as const
  }
);

export type AnimatedScrollViewProps = GetProps<typeof AnimatedScrollView>;

export const AnimatedScrollView = Animated.createAnimatedComponent(ScrollView);

export const ScrollViewStyles = StyleSheet.create({
  contentContainerStyle: {
    paddingBottom: 90
  }
});

export type ItemSeparatorProps<T> = {
  leadingItem: T;
  trailingItem?: T;
};

export type ScrollableRef = AnimatedRef<Animated.ScrollView>;
export type ScrollableContextType = {
  scrollableRef?: AnimatedRef<Animated.ScrollView>;
};

export const ScrollableContext = createContext<ScrollableContextType>(null as any);
export const ScrollableProvider = ({
  scrollableRef,
  children
}: PropsWithChildren<ScrollableContextType>) => {
  return (
    <ScrollableContext.Provider value={{ scrollableRef }}>{children}</ScrollableContext.Provider>
  );
};

/**
 * A custom hook to access the scrollable ref from the ScrollableProvider context.
 * @returns The scrollable ref of the parent ScrollableProvider. This is useful for accessing the scrollable ref from a child component.
 */
export const useParentScrollableRef = () => {
  const scrollableContext = use(ScrollableContext);

  if (!scrollableContext) {
    throw new Error('useParentScrollableRef must be used within a ScrollableProvider');
  }

  return scrollableContext.scrollableRef;
};
